import React, { useEffect, useState } from "react";
import ClientLayout from "../../../components/client/ClientLayout";
import RequireClientAdmin from "../../../components/client/RequireClientAdmin";
import ColorPicker from "../../../components/ColorPicker";
import AdminSelect from "../../../components/AdminSelect";
import SignupTextStyleEditor from "../../../components/SignupTextStyleEditor";
import SignupLivePreview from "../../../components/SignupLivePreview";
import SignupBenefitsEditor from "../../../components/SignupBenefitsEditor";
import SignupLayoutControls from "../../../components/SignupLayoutControls";
import { clientApi, adminApi } from "../../../api";
import { FONT_OPTIONS } from "../../../utils/signupTextStyle";

const COLORS = [
  ["primary_color",     "Primary colour",  "Buttons, links, prices & accents"],
  ["accent_color",      "Accent colour",   "Signup hero panel background"],
  ["background_color",  "Background",       "Backdrop behind the signup card"],
  ["text_color",        "Text",            "Body text on the form"],
  ["button_text_color", "Button text",     "Label colour on buttons"],
];

const TEXT_FIELDS = [
  ["name",             "Venue name",          "Used across the portal and emails"],
  ["welcome_title",    "Hero headline",       "Large heading in the signup hero panel"],
  ["welcome_subtitle", "Hero subtitle",       "Sub-line under the headline"],
  ["choice_title",     "First step title",    "Heading on the existing-vs-new step (blank = “Welcome!”)"],
  ["choice_subtitle",  "First step subtitle", "Blank uses the default question"],
];

export default function SettingsBranding() {
  return (
    <ClientLayout>
      <RequireClientAdmin>
        <Inner />
      </RequireClientAdmin>
    </ClientLayout>
  );
}

function Inner() {
  const [venue, setVenue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    clientApi.getVenue()
      .then((v) => setVenue({
        ...v,
        signup_text_styles: v.signup_text_styles || {},
        signup_layout: v.signup_layout || {},
      }))
      .catch((err) => setError(err.message));
  }, []);

  const set = (k, v) => setVenue((cur) => ({ ...cur, [k]: v }));

  async function save() {
    setSaving(true); setError(""); setSuccess("");
    try {
      const benefits = (venue.signup_benefits || [])
        .map((b) => ({ icon: b.icon || "check", label: (b.label || "").trim() }))
        .filter((b) => b.label);
      const updated = await clientApi.updateVenue({
        name: venue.name,
        logo_url: venue.logo_url,
        cover_image_url: venue.cover_image_url,
        primary_color: venue.primary_color,
        accent_color: venue.accent_color,
        background_color: venue.background_color,
        text_color: venue.text_color,
        button_text_color: venue.button_text_color,
        button_radius: venue.button_radius,
        font_family: venue.font_family,
        welcome_title: venue.welcome_title,
        welcome_subtitle: venue.welcome_subtitle,
        choice_title: venue.choice_title,
        choice_subtitle: venue.choice_subtitle,
        signup_benefits: benefits,
        signup_text_styles: venue.signup_text_styles || {},
        signup_layout: venue.signup_layout || {},
      });
      setVenue({
        ...updated,
        signup_text_styles: updated.signup_text_styles || {},
        signup_layout: updated.signup_layout || {},
      });
      setSuccess("Branding saved.");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  async function uploadImage(e, field) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const url = await adminApi.uploadImage(f);
      set(field, url);
    } catch (err) { setError(err.message); }
    setUploading(false);
  }

  if (error && !venue) return <div className="toast toast-error">{error}</div>;
  if (!venue) return <div className="loading-state"><div className="spinner" /></div>;

  return (
    <>
      <div className="venues-page-header">
        <div>
          <h1>Branding</h1>
          <p className="page-subtitle">Customise your public signup — layout, colours, fonts, logo, hero copy and value props. The preview is your real signup and updates as you edit.</p>
        </div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
      </div>

      {error && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {success && <div className="toast toast-success" style={{ marginBottom: "1rem" }}>{success}</div>}

      <SignupLivePreview venue={venue} slug={venue.slug} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1rem", marginTop: "1rem" }}>
        {/* Layout & hero */}
        <div className="settings-card">
          <div className="settings-card-header"><h3>Layout &amp; hero</h3><p>Structure of the signup — hero panel, sides &amp; corners</p></div>
          <SignupLayoutControls value={venue.signup_layout} onChange={(v) => set("signup_layout", v)} />
        </div>

        {/* Logo & hero image */}
        <div className="settings-card">
          <div className="settings-card-header"><h3>Logo &amp; hero image</h3><p>Shown in the signup hero panel</p></div>
          <div className="form-field">
            <label>Logo</label>
            {venue.logo_url && <img src={venue.logo_url} alt="Current logo" style={{ maxWidth: 160, maxHeight: 80, background: "var(--bg-elevated)", border: "1px solid var(--border)", padding: 8, borderRadius: "var(--radius-sm)", marginBottom: 8 }} />}
            <label className="btn btn-outline btn-sm" style={{ alignSelf: "flex-start", cursor: uploading ? "not-allowed" : "pointer" }}>
              {uploading ? "Uploading…" : venue.logo_url ? "Replace logo" : "Upload logo"}
              <input type="file" accept="image/*" onChange={(e) => uploadImage(e, "logo_url")} disabled={uploading} style={{ display: "none" }} />
            </label>
          </div>
          <div className="form-field">
            <label>Hero background image <span className="optional">— optional</span></label>
            {venue.cover_image_url && <img src={venue.cover_image_url} alt="Current hero background" style={{ width: "100%", maxHeight: 120, objectFit: "cover", borderRadius: "var(--radius-sm)", marginBottom: 8 }} />}
            <label className="btn btn-outline btn-sm" style={{ alignSelf: "flex-start", cursor: uploading ? "not-allowed" : "pointer" }}>
              {uploading ? "Uploading…" : venue.cover_image_url ? "Replace image" : "Upload image"}
              <input type="file" accept="image/*" onChange={(e) => uploadImage(e, "cover_image_url")} disabled={uploading} style={{ display: "none" }} />
            </label>
            <span className="field-hint">Sits faintly behind the hero gradient (strength set under Layout &amp; hero).</span>
          </div>
        </div>

        {/* Copy & font */}
        <div className="settings-card">
          <div className="settings-card-header"><h3>Copy &amp; font</h3><p>Headings, identity &amp; typography</p></div>
          {TEXT_FIELDS.map(([k, label, hint]) => (
            <div key={k} className="form-field">
              <label htmlFor={`brand-${k}`}>{label}</label>
              <input id={`brand-${k}`} value={venue[k] || ""} onChange={(e) => set(k, e.target.value)} />
              {hint && <span className="field-hint">{hint}</span>}
            </div>
          ))}
          <div className="form-field">
            <label>Font family</label>
            <AdminSelect value={venue.font_family || "Inter"} onChange={(v) => set("font_family", v)} options={FONT_OPTIONS.map((f) => ({ value: f, label: f }))} />
            <span className="field-hint">Applied across the signup.</span>
          </div>
          <div className="form-field">
            <label htmlFor="brand-button_radius">Button radius (px)</label>
            <input id="brand-button_radius" type="number" min={0} max={32} value={venue.button_radius ?? 8} onChange={(e) => set("button_radius", Number(e.target.value))} />
          </div>
        </div>

        {/* Per-line text styling */}
        <div className="settings-card">
          <div className="settings-card-header"><h3>Text styling</h3><p>Show/hide or restyle each headline</p></div>
          <SignupTextStyleEditor value={venue.signup_text_styles} onChange={(v) => set("signup_text_styles", v)} />
        </div>

        {/* Signup benefits */}
        <div className="settings-card">
          <div className="settings-card-header"><h3>Signup benefits</h3><p>The value props listed in the hero panel</p></div>
          <SignupBenefitsEditor value={venue.signup_benefits} onChange={(v) => set("signup_benefits", v)} />
        </div>

        {/* Colours */}
        <div className="settings-card">
          <div className="settings-card-header"><h3>Colours</h3><p>Brand palette for the signup</p></div>
          {COLORS.map(([k, label, hint]) => (
            <div key={k} className="form-field">
              <label>{label}</label>
              <ColorPicker value={venue[k] || "#000000"} onChange={(v) => set(k, v)} />
              {hint && <span className="field-hint">{hint}</span>}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
